import { useQuery } from '@tanstack/react-query';
import { SelectField } from '@/components/Form';
import { fetchPlans, planKeys } from '@/features/plans/api';

/** The option value standing for "no plan". */
export const NO_PLAN = '';

/**
 * Picks a subscription tier, shared by tenant create and tenant edit so the two
 * cannot drift in what they offer or in how they express "none".
 *
 * "No plan" is a real option, not an empty prompt: a shop may be onboarded
 * before its tier is agreed, and forcing a choice at creation would fabricate a
 * billing relationship. The value is the empty string, which both callers map
 * back to null before it reaches the API.
 *
 * While the list loads the select renders with only the current value. Plans
 * change roughly never, so this is a blink rather than a state worth building a
 * spinner for — and the field is always usable because "No plan" is always
 * present.
 */
export function PlanSelectField({
  id = 'planId',
  value,
  onChange,
  hint,
}: {
  id?: string;
  /** The selected plan id, or NO_PLAN. */
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  const query = useQuery({
    queryKey: planKeys.list(),
    queryFn: ({ signal }) => fetchPlans(signal),
    // Tiers are reference data — refetching them per mount is pure noise.
    staleTime: 5 * 60_000,
  });

  return (
    <SelectField
      id={id}
      label="Plan"
      hint={hint ?? 'Limits warn only — a tenant over their plan is never blocked.'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value={NO_PLAN}>No plan</option>
      {(query.data ?? []).map((plan) => (
        <option key={plan.id} value={plan.id}>
          {plan.name}
        </option>
      ))}
    </SelectField>
  );
}
